/**
 * LauncherPage — `/launcher`  (superall-shell-06)
 *
 * Sections
 * ─────────
 * 1. RecentJumpsRail   — horizontal scrollable strip of BUs the user visited
 *                        recently (derived from useActivitySummary + BU_SURFACES)
 * 2. QuickActionsRow   — top-up wallet, view loyalty, notifications shortcuts
 * 3. BUGrid            — full BU_SURFACES card grid with handoff on click
 *
 * Accessibility: every BU tile has an accessible name; the status badge is
 * aria-hidden (the text-only button label provides the full context); focus
 * rings are consistent with the rest of the shell.
 *
 * RTL: the recent-jumps rail scrolls horizontally in both directions via
 * overflow-x-auto (no logical-property workaround needed).
 */

import * as React from 'react';
import { Link } from 'wouter';
import { StaggerList, StaggerItem, MotionCard, FadeIn } from '@workspace/ui';

import { useActivitySummary } from '../hooks/useActivity';
import { BU_SURFACES, handoffToBU, formatRelative, type BUSurface } from '../lib/handoff';
import { STATUS_STYLES } from '../lib/badges';
import { useAuth } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';

// ── BU tile ───────────────────────────────────────────────────────────────────

interface BUTileProps {
  bu: BUSurface;
  lastActivityAt?: string | null;
  emphasis?: boolean;
}

function BUTile({ bu, lastActivityAt, emphasis = false }: BUTileProps) {
  const t = useT();
  const [lang] = useLang();
  const { token } = useAuth();
  const [opening, setOpening] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const name = lang === 'ar' ? bu.nameAr : bu.nameEn;
  const desc = lang === 'ar' ? bu.descAr : bu.descEn;
  const disabled = bu.status === 'soon' || !bu.handoffApiPath;
  const relative = formatRelative(lastActivityAt ?? null, lang);

  async function launch() {
    if (!token || disabled) return;
    setOpening(true);
    setErr(null);
    try {
      await handoffToBU(bu, token, import.meta.env.DEV);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t.handoffError);
    } finally {
      setOpening(false);
    }
  }

  return (
    <button
      type="button"
      disabled={disabled || opening}
      onClick={() => void launch()}
      aria-label={`${name} — ${disabled ? t.launcherPage.comingSoon : t.launcherPage.open}`}
      className={`
        flex flex-col rounded-2xl border bg-white dark:bg-neutral-900 px-4 py-4 text-start transition
        hover:border-purple-300 hover:shadow-sm
        focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2
        disabled:cursor-default disabled:opacity-60
        ${emphasis ? 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/40' : 'border-neutral-200 dark:border-neutral-700'}
      `}
    >
      <div className="mb-2 text-3xl" aria-hidden="true">
        {bu.icon}
      </div>
      <p className="font-semibold leading-tight text-neutral-900 dark:text-neutral-100">{name}</p>
      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{desc}</p>
      {relative && (
        <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
          {t.launcherPage.lastVisited} {relative}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[bu.status]}`}
          aria-hidden="true"
        >
          {bu.status === 'live' ? t.live : bu.status === 'beta' ? t.betaStatus : t.soon}
        </span>
        {opening && (
          <span className="text-xs text-purple-600 animate-pulse">{t.launcherPage.opening}</span>
        )}
      </div>
      {err && <p className="mt-1 text-xs text-red-600">{t.handoffError}</p>}
    </button>
  );
}

// ── recent chip ───────────────────────────────────────────────────────────────

function RecentChip({ bu, lastActivityAt }: { bu: BUSurface; lastActivityAt: string | null }) {
  const t = useT();
  const [lang] = useLang();
  const { token } = useAuth();
  const [opening, setOpening] = React.useState(false);
  const name = lang === 'ar' ? bu.nameAr : bu.nameEn;
  const relative = formatRelative(lastActivityAt, lang);

  async function launch() {
    if (!token || !bu.handoffApiPath) return;
    setOpening(true);
    try {
      await handoffToBU(bu, token, import.meta.env.DEV);
    } finally {
      setOpening(false);
    }
  }

  return (
    <button
      type="button"
      disabled={opening}
      onClick={() => void launch()}
      className="flex shrink-0 flex-col items-center rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 text-center hover:border-purple-300 hover:shadow-sm disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
    >
      <div className="mb-1 text-2xl" aria-hidden="true">
        {bu.icon}
      </div>
      <p className="text-xs font-semibold text-neutral-900 dark:text-neutral-100">{name}</p>
      {relative && (
        <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">{relative}</p>
      )}
      {opening && <p className="mt-0.5 text-xs text-purple-600 animate-pulse">{t.opening}</p>}
    </button>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function LauncherPage(): React.ReactElement {
  const t = useT();
  const { data: activity } = useActivitySummary();

  // Derive recent BUs (visited in the last 30 days, sorted by recency)
  const recentBUs = React.useMemo<{ bu: BUSurface; lastActivityAt: string | null }[]>(() => {
    if (!activity) return [];
    return Object.entries(activity.bus)
      .filter(([, e]) => e.lastActivityAt !== null)
      .sort(([, a], [, b]) => {
        const ta = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
        const tb = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 6)
      .map(([id, e]) => {
        const bu = BU_SURFACES.find((b) => b.id === id);
        return bu ? { bu, lastActivityAt: e.lastActivityAt } : null;
      })
      .filter((x): x is { bu: BUSurface; lastActivityAt: string | null } => x !== null);
  }, [activity]);

  // Derive activity map for BUTile last-visited label
  const activityMap = React.useMemo<Record<string, string | null>>(() => {
    if (!activity) return {};
    return Object.fromEntries(
      Object.entries(activity.bus).map(([id, e]) => [id, e.lastActivityAt])
    );
  }, [activity]);

  return (
    <FadeIn>
      <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
        {t.launcherPage.title}
      </h1>

      {/* ── Recent jumps rail ────────────────────────── */}
      {recentBUs.length > 0 && (
        <section aria-label={t.launcherPage.recentJumps} className="mb-6">
          <h2 className="mb-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
            {t.launcherPage.recentJumps}
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory">
            {recentBUs.map(({ bu, lastActivityAt }) => (
              <div key={bu.id} className="snap-start">
                <RecentChip bu={bu} lastActivityAt={lastActivityAt} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Quick actions row ────────────────────────── */}
      <section aria-label={t.launcherPage.quickActions} className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {t.launcherPage.quickActions}
        </h2>
        <div className="flex gap-2 flex-wrap">
          {(
            [
              { href: '/wallet', emoji: '💳', label: t.launcherPage.topUpWallet },
              { href: '/loyalty', emoji: '⭐', label: t.launcherPage.viewLoyalty },
              {
                href: '/notifications',
                emoji: '🔔',
                label: t.launcherPage.viewNotifications,
              },
            ] as const
          ).map((action) => (
            <Link
              key={action.href}
              href={action.href}
              className="flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-purple-300 hover:text-purple-700 dark:hover:text-purple-400 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            >
              <span aria-hidden="true">{action.emoji}</span>
              {action.label}
            </Link>
          ))}
        </div>
      </section>

      {/* ── Full BU grid ─────────────────────────────── */}
      <section aria-label={t.launcherPage.allServices}>
        <h2 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {t.launcherPage.allServices}
        </h2>
        <StaggerList className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {BU_SURFACES.map((bu) => (
            <StaggerItem key={bu.id}>
              <MotionCard hoverScale={1.03} tapScale={0.97} className="h-full">
                <BUTile bu={bu} lastActivityAt={activityMap[bu.id] ?? null} />
              </MotionCard>
            </StaggerItem>
          ))}
        </StaggerList>
      </section>
    </FadeIn>
  );
}
